import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import useSWR from 'swr';
import qs from 'qs';
import isNil from 'lodash/isNil';
import {
  locationT,
  Marketplace,
  OutstandingInvoices,
} from '@ordermentum/ui-types';
import { fetcher } from '@ordermentum/ui-shared/swr/fetchers';
import { NewPaymentMethod } from '@ordermentum/ui-shared/organisms/PaymentMethods';
import Button from '@ordermentum/ui-shared/components/Button/Button';
import Spinner from '@ordermentum/ui-shared/foundations/Spinner';
import { IMAGE_URL } from '@ordermentum/ui-constants';
import { Invoice, ToastReduxAction } from '@ordermentum/ui-definitions';
import { PaymentMethodType } from '@ordermentum/ui-types/src/purchaser';
import { useFlags } from '@ordermentum/ui-shared/hooks/useFlags';
import {
  addNewDirect,
  DirectDebitParams,
} from '@ordermentum/ui-shared/redux/payment_methods';
import * as LocalStorageUtility from '@ordermentum/ui-shared/utilities/localStorage';
import { Icon } from '@ordermentum/ui-shared/foundations';
import { handleExportPdf } from '../../redux/actions';
import history from '../../lib/history';
import { SUPPLIER_ASYNC_PAYMENTS } from '../Home';
import { showToast } from '../../redux/actions/toast';

type Props = {
  location: locationT;
  params: any;
  paymentType: any[];
  handleExport: (paymentRequestId?: string, token?: string) => void;
  addDirectPayment: (
    directDetails: DirectDebitParams,
    retailerId: string
  ) => Promise<void>;
  showToastAction: (toast: ToastReduxAction) => Promise<void>;
};

export const Footer = () => (
  <div className="flex items-center justify-center gap-x-xs border border-solid border-background-subtle py-l">
    <p className="font-body text-body-small text-subtle">Powered by</p>
    <img className="h-6" src={`${IMAGE_URL}/logo-ordermentum-full.svg`} />
  </div>
);

export const PaymentRequest = ({
  params,
  location,
  paymentType,
  showToastAction,
  handleExport,
  addDirectPayment,
}: Props) => {
  const { retailerId } = params;
  const { paymentRequestId, invoiceId, userId, token, supplierId } =
    location.query;

  const flags = useFlags([SUPPLIER_ASYNC_PAYMENTS], {
    supplierId,
    source: 'web',
  });

  const { data: outstandingInvoices } = useSWR<OutstandingInvoices>(
    `/v1/payment-requests/${paymentRequestId}/outstanding-invoices?${qs.stringify(
      { token }
    )}`,
    fetcher()
  );

  let apiUrl = `/v1/retailers/${retailerId}`;
  if (paymentRequestId && token) {
    const qst = qs.stringify({
      retailerId,
      token,
    });
    apiUrl = `/v1/payment-requests/${paymentRequestId}?${qst}`;
  }

  const { data, error, isLoading } = useSWR<{
    id: string;
    purchaser: Marketplace;
    invoice?: Invoice;
  }>(apiUrl, fetcher());

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const invoices = outstandingInvoices?.data?.invoices ?? [];
  const totalOutstandingInvoices = invoices.length;
  const source =
    totalOutstandingInvoices > 0 ? 'outstanding-invoices' : 'payment-request';

  const onSuccess = (
    paymentId: string,
    userData?: {
      signatory: string;
      email: string;
      phone: string;
    }
  ) => {
    const redirectUrl =
      totalOutstandingInvoices > 0
        ? `/payments/${retailerId}/outstanding-invoices`
        : `/payments/${retailerId}/auth`;

    if (userData && !userId) {
      LocalStorageUtility.setItem(
        `dd-$${paymentRequestId}`,
        JSON.stringify(userData)
      );
    }
    history.push({
      pathname: redirectUrl,
      query: {
        paymentRequestId,
        invoiceId,
        userId,
        token,
      },
    });
  };

  const paymentMethodTypes = useMemo(
    () => data?.purchaser?.paymentMethodTypes?.value ?? [],
    [data?.purchaser?.paymentMethodTypes?.value]
  );
  const canSwitch =
    paymentMethodTypes.includes('card') &&
    paymentMethodTypes.includes('direct');

  const [accountType, setAccountType] = useState<PaymentMethodType>('card');
  const isCard = accountType === 'card';

  useEffect(() => {
    const defaultAccountType =
      paymentMethodTypes.length > 0 && !paymentMethodTypes.includes('card')
        ? 'direct'
        : 'card';
    setAccountType(defaultAccountType);
  }, [paymentMethodTypes]);

  if (isLoading || !flags)
    return (
      <div>
        <Spinner size="M" />
      </div>
    );

  if (error || !data)
    return (
      <div className="flex h-screen flex-col items-center justify-center font-body text-body font-bold">
        <div>Failed to load required data</div>
        <div>
          <a href="/" className="text-primary-brand hover:underline">
            Go home
          </a>
        </div>
      </div>
    );

  const isInvoice = data.invoice && !isNil(invoiceId);
  const { purchaser, invoice } = data;
  const { supplier, retailer } = purchaser;

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-regular">
      <div className="flex justify-center px-s pb-xl pt-16">
        <div className="flex w-full max-w-screen-sm flex-col self-stretch">
          {supplier && (
            <div className="flex flex-col items-center gap-xxs">
              <img
                className="size-14 max-h-24 rounded border border-solid border-background-subtle"
                src={supplier.logo?.mini}
                alt={`${supplier.tradingName} logo`}
              />
              <h2 className="text-center font-heading text-h2">
                {isInvoice
                  ? `Invoice payment to ${supplier.tradingName}`
                  : `${supplier.tradingName} would like you to pay through Ordermentum`}
              </h2>
            </div>
          )}
          {isInvoice ? (
            <div className="flex flex-col items-center gap-s pt-s">
              <p className="font-body text-body text-subtle">
                Invoice #{invoice.number}
              </p>
              <h3 className="text-3xl font-bold">
                ${data.invoice.display?.totalDue}
              </h3>
              <Button
                icon={<Icon name="mDownload" inheritFill />}
                variant="tertiary"
                type="button"
                onClick={() => {
                  handleExport(paymentRequestId, token);
                }}
              >
                Download invoice
              </Button>
            </div>
          ) : (
            <span className="pt-s font-body text-body">
              Simply enter your {isCard ? 'card' : 'direct debit'} details
              future orders for <strong>{retailer.tradingName}</strong> will
              will use this payment method by default.
            </span>
          )}
          {canSwitch && !isInvoice && (
            <div>
              <Button
                variant="text-link"
                type="reset"
                onClick={() => {
                  setAccountType(isCard ? 'direct' : 'card');
                }}
              >
                {isCard
                  ? 'Switch to a direct debit account'
                  : 'Switch to a credit card'}
              </Button>
            </div>
          )}

          <NewPaymentMethod
            showToast={showToastAction}
            params={params}
            type={accountType}
            location={location}
            history={history}
            purchaserId={purchaser.id}
            retailer={purchaser.retailer}
            onSuccess={onSuccess}
            paymentType={paymentType}
            addDirectPayment={addDirectPayment}
            source={source}
            showAuthority
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

const mapDispatchToProps = {
  handleExport: handleExportPdf,
  addDirectPayment: addNewDirect,
  showToastAction: showToast,
};

const PaymentRequestConnected = connect(
  null,
  mapDispatchToProps
)(PaymentRequest);

export { PaymentRequestConnected };
